import { HttpClient } from '@angular/common/http';
import { Component, OnInit, signal } from '@angular/core';
import { switchMap } from 'rxjs';
import { environment } from 'src/environments/environment';
import AzureService from 'src/app/shared/services/azure.service';
import { SignalrService } from 'src/app/shared/services/signalr.service';
import { IntegrationTestStatus } from '../../models/integration-test-status';
import StartIntegrationTestsPayload from '../../models/start-integration-tests-payload';
import { StartIntegrationTestsResponse } from '../../models/start-integration-tests-response';

type ConnectionDetailsSignalR = {
  id: string;
  qrCode?: string
  whatsAppStatus: number;
}

@Component({
  standalone: false,
  selector: 'app-integration-test',
  templateUrl: './integration-test.component.html',
  styleUrls: ['./integration-test.component.css']
})
export class IntegrationTestComponent implements OnInit {
  readonly tests = signal<IntegrationTest[]>([]);
  readonly selectedTest = signal<IntegrationTest | null>(null);
  expandedTestIds = new Set<string>();
  private subscribedConnectionIds = new Set<string>();
  targetPhoneNumber = '';

  constructor(
    private readonly httpClient: HttpClient,
    private readonly azureService: AzureService,
    private readonly signalRService: SignalrService
  ) {
  }

  ngOnInit() {
    this.loadTests();
  }

  selectTest(test: IntegrationTest): void {
    this.selectedTest.set(test);
  }

  isTestExpanded(test: IntegrationTest): boolean {
    return this.expandedTestIds.has(test.id);
  }

  toggleTestDetails(test: IntegrationTest, event?: Event): void {
    event?.stopPropagation();
    if (this.expandedTestIds.has(test.id)) {
      this.expandedTestIds.delete(test.id);
      return;
    }

    this.expandedTestIds.add(test.id);
  }

  startNewTest(): void {
    const targetPhoneNumber = (this.targetPhoneNumber ?? '').trim();
    if (!targetPhoneNumber) {
      return;
    }

    this.azureService.getUserProfile().pipe(
      switchMap(profile => {
        const payload = new StartIntegrationTestsPayload(profile.userPrincipalName, targetPhoneNumber);
        return this.httpClient.post<StartIntegrationTestsResponse>(
          `${environment.whatsAppApi}/infrastructure/connections/integrationtests`,
          payload
        );
      })
    ).subscribe({
      next: (row: StartIntegrationTestsResponse) => {
        this.applyIntegrationTestRow(row);
        this.ensureSignalrForConnection(row.connectionId);

        const startedTest = this.tests().find(t => t.id === row.id);
        if (startedTest) {
          this.selectedTest.set(startedTest);
          this.expandedTestIds.add(startedTest.id);
        }
      }
    });
  }

  private loadTests(): void {
    this.httpClient.get<StartIntegrationTestsResponse[]>(`${environment.whatsAppApi}/infrastructure/connections/integrationtests`).subscribe({
      next: (response) => {
        const loadedTests = this.mapRowsToIntegrationTests(response);
        this.tests.set(this.mergeTestsPreservingRealtime(this.tests(), loadedTests));
        this.selectedTest.set(this.tests()[0] ?? null);
        this.expandedTestIds.clear();

        for (const test of loadedTests.filter(t => t.status === IntegrationTestStatus.running)) {
          for (const conn of test.connectionsStatus) {
            this.ensureSignalrForConnection(conn.connectionId);
          }
        }
      },
      error: () => {
        this.tests.set([]);
        this.selectedTest.set(null);
        this.expandedTestIds.clear();
      }
    });
  }

  private mapRowsToIntegrationTests(rows: StartIntegrationTestsResponse[]): IntegrationTest[] {
    const byId = new Map<string, IntegrationTest>();

    for (const row of rows ?? []) {
      if (!row?.id) {
        continue;
      }

      const existing = byId.get(row.id);
      if (!existing) {
        byId.set(row.id, {
          id: row.id,
          createdAt: row.createdAt,
          startedBy: row.startedBy,
          status: row.status as IntegrationTestStatus,
          connectionsStatus: []
        });
      }

      const test = byId.get(row.id)!;
      test.status = row.status as IntegrationTestStatus;
      test.createdAt = row.createdAt;
      test.startedBy = row.startedBy;

      if (row.connectionId && !test.connectionsStatus.some(c => c.connectionId === row.connectionId)) {
        test.connectionsStatus.push(this.mapRowToConnectionStatus(row));
      }
    }

    return Array.from(byId.values()).sort((a, b) => {
      const aTime = new Date(a.createdAt).getTime();
      const bTime = new Date(b.createdAt).getTime();
      return bTime - aTime;
    });
  }

  private mapRowToConnectionStatus(row: StartIntegrationTestsResponse): IntegrationTestConnectionStatus {
    return {
      connectionId: row.connectionId,
      qrCode: undefined,
      wahaImage: row.wahaImage,
      chartVersion: row.niboWAHAChartVersion,
      niboWahaVersion: row.niboWAHAImage,
      whatsAppStatus: undefined,
      steps: [
        { key: 'qrScanned', label: 'QRCode escaneado', done: row.qrCodeScanned },
        { key: 'messageSent', label: 'Mensagem enviada', done: row.messageSent },
        { key: 'ackReceived', label: 'ACK recebido', done: row.ackReceived },
        { key: 'messageReceived', label: 'Mensagem recebida', done: row.messageReceived },
        { key: 'fileSent', label: 'Arquivo enviado', done: row.fileSent },
        { key: 'fileReceived', label: 'Arquivo recebido', done: row.fileReceived }
      ]
    };
  }

  private applyIntegrationTestRow(row: StartIntegrationTestsResponse): void {
    if (!row?.id) {
      return;
    }

    const existingTest = this.tests().find(t => t.id === row.id);
    if (!existingTest) {
      const newTest: IntegrationTest = {
        id: row.id,
        createdAt: row.createdAt,
        startedBy: row.startedBy,
        status: row.status as IntegrationTestStatus,
        connectionsStatus: row.connectionId ? [this.mapRowToConnectionStatus(row)] : []
      };

      this.tests.update(current => [newTest, ...current]);
      return;
    }

    this.mutateExistingTest(existingTest, row);
    this.notifyTestsMutatedInPlace();
  }

  private mutateExistingTest(existingTest: IntegrationTest, row: StartIntegrationTestsResponse): void {
    existingTest.status = row.status as IntegrationTestStatus;
    existingTest.createdAt = row.createdAt;
    existingTest.startedBy = row.startedBy;

    if (!row.connectionId) {
      return;
    }

    const existingConn = existingTest.connectionsStatus.find(c => c.connectionId === row.connectionId);
    if (!existingConn) {
      existingTest.connectionsStatus.push(this.mapRowToConnectionStatus(row));
      return;
    }

    const nextConn = this.mapRowToConnectionStatus(row);
    existingConn.chartVersion = nextConn.chartVersion;
    existingConn.wahaImage = nextConn.wahaImage;
    existingConn.niboWahaVersion = nextConn.niboWahaVersion;
    existingConn.steps = nextConn.steps;
  }

  private mergeTestsPreservingRealtime(
    existing: IntegrationTest[],
    incoming: IntegrationTest[]
  ): IntegrationTest[] {
    const existingById = new Map(existing.map(t => [t.id, t] as const));

    return incoming.map(nextTest => {
      const prevTest = existingById.get(nextTest.id);
      if (!prevTest) {
        return nextTest;
      }

      const prevConnById = new Map(prevTest.connectionsStatus.map(c => [c.connectionId, c] as const));
      const mergedConnections = nextTest.connectionsStatus.map(nextConn => {
        const prevConn = prevConnById.get(nextConn.connectionId);
        if (!prevConn) {
          return nextConn;
        }

        return {
          ...nextConn,
          qrCode: prevConn.qrCode ?? nextConn.qrCode,
          whatsAppStatus: prevConn.whatsAppStatus ?? nextConn.whatsAppStatus
        };
      });

      return {
        ...nextTest,
        connectionsStatus: mergedConnections
      };
    });
  }

  private upsertTestPreservingRealtime(test: IntegrationTest): void {
    const existingIndex = this.tests().findIndex(t => t.id === test.id);
    if (existingIndex === -1) {
      this.tests.update(current => [test, ...current]);
      return;
    }

    const merged = this.mergeTestsPreservingRealtime([this.tests()[existingIndex]], [test])[0];
    const next = [...this.tests()];
    next.splice(existingIndex, 1, merged);
    this.tests.set(next);
  }

  isConnectionDone(conn: IntegrationTestConnectionStatus): boolean {
    return conn.steps.every(s => s.done);
  }

  getTestStatusLabel(test: IntegrationTest): string {
    switch (test.status) {
      case IntegrationTestStatus.succeded:
        return 'Sucesso';
      case IntegrationTestStatus.failed:
        return 'Falhou';
      case IntegrationTestStatus.expired:
        return 'Expirado';
      case IntegrationTestStatus.running:
      default:
        return 'Pendente';
    }
  }

  getTestStatusBadgeClass(test: IntegrationTest): string {
    switch (test.status) {
      case IntegrationTestStatus.succeded:
        return 'bg-success';
      case IntegrationTestStatus.failed:
        return 'bg-danger';
      case IntegrationTestStatus.expired:
        return 'bg-warning text-dark';
      case IntegrationTestStatus.running:
      default:
        return 'bg-secondary';
    }
  }

  private updateTestsQrCode(connectionId: string, qrCode: string | undefined): void {
    for (const test of this.tests()) {
      const connStatus = test.connectionsStatus.find(c => c.connectionId === connectionId);
      if (connStatus) {
        connStatus.qrCode = qrCode;
      }
    }

    this.notifyTestsMutatedInPlace();
  }

  private updateWhatsAppStatus(connectionId: string, whatsAppStatus: number): void {
    for (const test of this.tests()) {
      const connStatus = test.connectionsStatus.find(c => c.connectionId === connectionId);
      if (connStatus) {
        connStatus.whatsAppStatus = whatsAppStatus;
      }
    }

    this.notifyTestsMutatedInPlace();
  }

  // Estes updates do SignalR alteram campos dentro dos objetos de connectionsStatus sem
  // trocar a referencia do array, e um signal so notifica na troca de referencia. Trocar
  // por uma copia rasa faz o template reagir -- do contrario o QRCode e o status do
  // WhatsApp em tempo real nunca apareceriam sob change detection zoneless.
  private notifyTestsMutatedInPlace(): void {
    this.tests.update(current => [...current]);
  }

  private ensureSignalrForConnection(connectionId: string): void {
    if (this.subscribedConnectionIds.has(connectionId)) {
      return;
    }

    this.subscribedConnectionIds.add(connectionId);
    this.signalRService.connect(connectionId).then(() => { });

    this.signalRService.on(connectionId, 'connectiondetails', (data: ConnectionDetailsSignalR) => {
      const normalizedQrCode = this.normalizeQrCode(data.qrCode);
      this.updateTestsQrCode(data.id, normalizedQrCode);
      this.updateWhatsAppStatus(data.id, data.whatsAppStatus);
      console.log('Received connection details for', data);
    });

    this.signalRService.on(connectionId, 'IntegrationTestsResult', (data: StartIntegrationTestsResponse) => {
      this.applyIntegrationTestRow(data);

      // Se o backend emitir resultados de outras conexões via broadcast,
      // isso descobre o connectionId e garante que passamos a assinar também.
      if (data?.connectionId) {
        this.ensureSignalrForConnection(data.connectionId);
      }
    });
  }

  private normalizeQrCode(qrCode?: string | null): string | undefined {
    if (!qrCode) {
      return undefined;
    }

    return qrCode.startsWith('data:image/')
      ? qrCode
      : `data:image/png;base64,${qrCode}`;
  }
}

interface IntegrationTest {
  id: string;
  createdAt: string;
  startedBy: string;
  status: IntegrationTestStatus;
  connectionsStatus: IntegrationTestConnectionStatus[];
}

interface IntegrationStep {
  key: 'qrScanned' | 'messageSent' | 'ackReceived' | 'messageReceived' | 'fileSent' | 'fileReceived';
  label: string;
  done: boolean;
}

interface IntegrationConnection {
  id: string;
}

interface IntegrationTestConnectionStatus {
  connectionId: string;
  qrCode?: string;
  chartVersion: string;
  wahaImage: string;
  niboWahaVersion: string;
  steps: IntegrationStep[];
  whatsAppStatus?: number;
}
